// src/pages/CreateUnitPage.jsx
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { createUnit } from "../api/unitApi";
import { getAddressById, lookupAddress } from "../api/propertyApi";

import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  FormControlLabel,
  Switch,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

function CreateUnitPage() {
  const navigate = useNavigate();
  const { propertyId } = useParams();

  const [form, setForm] = useState({
    unit_code: "",
    unit_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    county: "",
    postcode: "",
    ideal_postcodes_id: "",
    uprn: "",
    address_latitude: "",
    address_longitude: "",
    correspondence_address_line1: "",
    correspondence_address_line2: "",
    correspondence_city: "",
    correspondence_county: "",
    correspondence_postcode: "",
    correspondence_ideal_postcodes_id: "",
    correspondence_uprn: "",
    correspondence_address_latitude: "",
    correspondence_address_longitude: "",
    has_different_correspondence_address: false,
    floor: "",
    square_feet: "",
    owner_name: "",
    mobile: "",
    email: "",
  });

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [unitLookupPostcode, setUnitLookupPostcode] = useState("");
  const [unitLookupFilter, setUnitLookupFilter] = useState("");
  const [unitLookupResults, setUnitLookupResults] = useState([]);
  const [unitLookupLoading, setUnitLookupLoading] = useState(false);
  const [unitLookupError, setUnitLookupError] = useState("");
  const [unitSelectedLookup, setUnitSelectedLookup] = useState("");
  const [corrLookupPostcode, setCorrLookupPostcode] = useState("");
  const [corrLookupFilter, setCorrLookupFilter] = useState("");
  const [corrLookupResults, setCorrLookupResults] = useState([]);
  const [corrLookupLoading, setCorrLookupLoading] = useState(false);
  const [corrLookupError, setCorrLookupError] = useState("");
  const [corrSelectedLookup, setCorrSelectedLookup] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        name === "square_feet"
          ? value.replace(/[^\d]/g, "")
          : value,
    }));
  };

  const handleCorrespondenceToggle = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({
      ...prev,
      has_different_correspondence_address: checked,
      ...(checked
        ? {}
        : {
            correspondence_address_line1: "",
            correspondence_address_line2: "",
            correspondence_city: "",
            correspondence_county: "",
            correspondence_postcode: "",
            correspondence_ideal_postcodes_id: "",
            correspondence_uprn: "",
            correspondence_address_latitude: "",
            correspondence_address_longitude: "",
          }),
    }));
    if (!checked) {
      setCorrLookupResults([]);
      setCorrSelectedLookup("");
      setCorrLookupError("");
    }
  };

  const handleUnitLookup = async () => {
    setUnitLookupError("");
    setUnitLookupResults([]);
    setUnitSelectedLookup("");
    const trimmed = unitLookupPostcode.trim();
    if (!trimmed) {
      setUnitLookupError("Enter a postcode to search.");
      return;
    }
    setUnitLookupLoading(true);
    try {
      const data = await lookupAddress(trimmed, unitLookupFilter.trim());
      const suggestions = data?.suggestions || [];
      setUnitLookupResults(suggestions);
      if (!suggestions.length) {
        setUnitLookupError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setUnitLookupError(err?.message || "Address lookup failed.");
    } finally {
      setUnitLookupLoading(false);
    }
  };

  const handleUnitSelectLookup = async (value) => {
    setUnitSelectedLookup(value);
    setUnitLookupError("");
    const selected = unitLookupResults[Number(value)] || null;
    if (!selected?.id) return;
    setForm((prev) => ({
      ...prev,
      address_line1: "",
      address_line2: "",
      city: "",
      county: "",
      postcode: "",
      ideal_postcodes_id: "",
      uprn: "",
      address_latitude: "",
      address_longitude: "",
    }));
    try {
      const address = await getAddressById(selected.id);
      setForm((prev) => ({
        ...prev,
        address_line1: address.line_1 || prev.address_line1,
        address_line2: address.line_2 || prev.address_line2,
        city: address.town_or_city || address.locality || prev.city,
        county: address.county || prev.county,
        postcode: address.postcode || unitLookupPostcode.trim() || prev.postcode,
        ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        uprn: address.uprn || "",
        address_latitude: address.latitude ?? "",
        address_longitude: address.longitude ?? "",
      }));
    } catch (err) {
      console.error("Address get error:", err);
      setUnitLookupError(err?.message || "Address lookup failed.");
    }
  };

  const handleCorrLookup = async () => {
    if (!form.has_different_correspondence_address) {
      return;
    }
    setCorrLookupError("");
    setCorrLookupResults([]);
    setCorrSelectedLookup("");
    const trimmed = corrLookupPostcode.trim();
    if (!trimmed) {
      setCorrLookupError("Enter a postcode to search.");
      return;
    }
    setCorrLookupLoading(true);
    try {
      const data = await lookupAddress(trimmed, corrLookupFilter.trim());
      const suggestions = data?.suggestions || [];
      setCorrLookupResults(suggestions);
      if (!suggestions.length) {
        setCorrLookupError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setCorrLookupError(err?.message || "Address lookup failed.");
    } finally {
      setCorrLookupLoading(false);
    }
  };

  const handleCorrSelectLookup = async (value) => {
    setCorrSelectedLookup(value);
    setCorrLookupError("");
    const selected = corrLookupResults[Number(value)] || null;
    if (!selected?.id) return;
    setForm((prev) => ({
      ...prev,
      correspondence_address_line1: "",
      correspondence_address_line2: "",
      correspondence_city: "",
      correspondence_county: "",
      correspondence_postcode: "",
      correspondence_ideal_postcodes_id: "",
      correspondence_uprn: "",
      correspondence_address_latitude: "",
      correspondence_address_longitude: "",
    }));
    try {
      const address = await getAddressById(selected.id);
      setForm((prev) => ({
        ...prev,
        correspondence_address_line1:
          address.line_1 || prev.correspondence_address_line1,
        correspondence_address_line2:
          address.line_2 || prev.correspondence_address_line2,
        correspondence_city:
          address.town_or_city || address.locality || prev.correspondence_city,
        correspondence_county:
          address.county || prev.correspondence_county,
        correspondence_postcode:
          address.postcode ||
          corrLookupPostcode.trim() ||
          prev.correspondence_postcode,
        correspondence_ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        correspondence_uprn: address.uprn || "",
        correspondence_address_latitude: address.latitude ?? "",
        correspondence_address_longitude: address.longitude ?? "",
      }));
    } catch (err) {
      console.error("Address get error:", err);
      setCorrLookupError(err?.message || "Address lookup failed.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        unit_code: form.unit_code,
        unit_name: form.unit_name,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        county: form.county,
        postcode: form.postcode,
        ideal_postcodes_id: form.ideal_postcodes_id || null,
        uprn: form.uprn || null,
        address_latitude: form.address_latitude || null,
        address_longitude: form.address_longitude || null,
        correspondence_address_line1: form.correspondence_address_line1,
        correspondence_address_line2: form.correspondence_address_line2,
        correspondence_city: form.correspondence_city,
        correspondence_county: form.correspondence_county,
        correspondence_postcode: form.correspondence_postcode,
        correspondence_ideal_postcodes_id:
          form.correspondence_ideal_postcodes_id || null,
        correspondence_uprn: form.correspondence_uprn || null,
        correspondence_address_latitude:
          form.correspondence_address_latitude || null,
        correspondence_address_longitude:
          form.correspondence_address_longitude || null,
        has_different_correspondence_address:
          form.has_different_correspondence_address,
        floor: form.floor,
        square_feet: form.square_feet ? Number(form.square_feet) : null,
        owner_name: form.owner_name,
        mobile: form.mobile,
        email: form.email,
      };

      await createUnit(propertyId, payload);
      navigate(`/properties/${propertyId}/details`, {
        state: {
          toast: {
            message: "Unit created",
            severity: "success",
          },
        },
      });
    } catch (err) {
      console.error("Create unit error:", err);
      setToast({
        open: true,
        message: "Failed to create unit. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box
        className="page-container"
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 600 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Create New Unit
          </Typography>

          <Typography sx={{ mb: 2, color: "text.secondary" }}>
            Property ID: {propertyId}
          </Typography>

          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Unit Code"
                name="unit_code"
                value={form.unit_code}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                required
                label="Unit Name"
                name="unit_name"
                value={form.unit_name}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                inputProps={{ maxLength: 255 }}
              />

              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Unit Address
                </Typography>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Postcode"
                      value={unitLookupPostcode}
                      onChange={(e) => setUnitLookupPostcode(e.target.value)}
                      disabled={loading || unitLookupLoading}
                      fullWidth
                    />
                    <TextField
                      label="Filter (optional)"
                      value={unitLookupFilter}
                      onChange={(e) => setUnitLookupFilter(e.target.value)}
                      disabled={loading || unitLookupLoading}
                      fullWidth
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    onClick={handleUnitLookup}
                    disabled={loading || unitLookupLoading}
                    startIcon={<SearchIcon />}
                    sx={{ alignSelf: "flex-start", minWidth: 180 }}
                  >
                    {unitLookupLoading ? "Searching..." : "Find Address"}
                  </Button>
                  {unitLookupError && (
                    <Alert severity="error">{unitLookupError}</Alert>
                  )}
                  <Collapse in={unitLookupResults.length > 0} timeout={300}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {unitLookupResults.length}{" "}
                      {unitLookupResults.length === 1 ? "address" : "addresses"}{" "}
                      found. Choose one from the dropdown.
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Select Address</InputLabel>
                      <Select
                        value={unitSelectedLookup}
                        label="Select Address"
                        onChange={(e) => handleUnitSelectLookup(e.target.value)}
                      >
                        {unitLookupResults.map((addr, index) => {
                          const label = addr.address || [addr.id].filter(Boolean).join(", ");
                          return (
                            <MenuItem key={`${index}-${label}`} value={String(index)}>
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Collapse>
                  {[
                    { label: "Address Line 1", name: "address_line1" },
                    { label: "Address Line 2", name: "address_line2" },
                    { label: "City", name: "city" },
                    { label: "County", name: "county" },
                    { label: "Postcode", name: "postcode" },
                  ].map(({ label, name }) => (
                    <TextField
                      key={name}
                      fullWidth
                      label={label}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      margin="normal"
                      disabled={loading}
                      inputProps={{ maxLength: name === "postcode" ? 20 : 255 }}
                    />
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Correspondence Address
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.has_different_correspondence_address}
                      onChange={handleCorrespondenceToggle}
                      disabled={loading}
                    />
                  }
                  label="Has a different correspondence address"
                  sx={{ mb: 1 }}
                />
                {form.has_different_correspondence_address && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Postcode"
                      value={corrLookupPostcode}
                      onChange={(e) => setCorrLookupPostcode(e.target.value)}
                      disabled={loading || corrLookupLoading}
                      fullWidth
                    />
                    <TextField
                      label="Filter (optional)"
                      value={corrLookupFilter}
                      onChange={(e) => setCorrLookupFilter(e.target.value)}
                      disabled={loading || corrLookupLoading}
                      fullWidth
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    onClick={handleCorrLookup}
                    disabled={loading || corrLookupLoading}
                    startIcon={<SearchIcon />}
                    sx={{ alignSelf: "flex-start", minWidth: 180 }}
                  >
                    {corrLookupLoading ? "Searching..." : "Find Address"}
                  </Button>
                  {corrLookupError && (
                    <Alert severity="error">{corrLookupError}</Alert>
                  )}
                  <Collapse in={corrLookupResults.length > 0} timeout={300}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {corrLookupResults.length}{" "}
                      {corrLookupResults.length === 1 ? "address" : "addresses"}{" "}
                      found. Choose one from the dropdown.
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Select Address</InputLabel>
                      <Select
                        value={corrSelectedLookup}
                        label="Select Address"
                        onChange={(e) => handleCorrSelectLookup(e.target.value)}
                      >
                        {corrLookupResults.map((addr, index) => {
                          const label = addr.address || [addr.id].filter(Boolean).join(", ");
                          return (
                            <MenuItem key={`${index}-${label}`} value={String(index)}>
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Collapse>
                  {[
                    {
                      label: "Address Line 1",
                      name: "correspondence_address_line1",
                    },
                    {
                      label: "Address Line 2",
                      name: "correspondence_address_line2",
                    },
                    { label: "City", name: "correspondence_city" },
                    { label: "County", name: "correspondence_county" },
                    { label: "Postcode", name: "correspondence_postcode" },
                  ].map(({ label, name }) => (
                    <TextField
                      key={name}
                      fullWidth
                      label={label}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      margin="normal"
                      disabled={loading}
                      inputProps={{ maxLength: name.endsWith("postcode") ? 20 : 255 }}
                    />
                  ))}
                </Stack>
                )}
              </Paper>

              <TextField
                fullWidth
                label="Floor"
                name="floor"
                value={form.floor}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                label="Square Feet"
                name="square_feet"
                value={form.square_feet}
                onChange={handleChange}
                margin="normal"
                inputMode="numeric"
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Owner Name"
                name="owner_name"
                value={form.owner_name}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                inputProps={{ maxLength: 500 }}
              />

              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                inputProps={{ maxLength: 255 }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.2,
                  bgcolor: "primary.main",
                  fontWeight: 600,
                }}
              >
                {loading ? "Creating..." : "Create Unit"}
              </Button>
            </form>
          </Paper>
        </Box>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default CreateUnitPage;
